import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'
import { useFlows } from '../hooks/useFlows'
import { useScenario } from '../hooks/useScenario'
import { useSmartBack } from '../hooks/useSmartBack'
import { useCommand } from '../hooks/useCommand'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { AccessDeniedBanner } from '../components/AccessDeniedBanner'
import { FormModal } from '../components/FormModal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import type { Flow } from '../../core/domain/models'
import { getRandomFlowColor, resolveFlowColor } from '../../core/domain/flowColors'
import { FlowColorPicker } from '../components/FlowColorPicker'
import './Flows.css'
import { FlowIcon } from '../components/FlowIcon'

export default function Flows() {
  const { scenarioId } = useParams<{ scenarioId: string }>()
  const navigate = useNavigate()
  const backToScenario = useSmartBack(scenarioId ? `/scenario/${scenarioId}` : '/')
  // Use passive mode because ScenarioLayout handles the hydration/subscription
  const { scenario, loading: scenarioLoading } = useScenario(scenarioId || '', { passive: true })
  const { flows, loading: flowsLoading } = useFlows(scenarioId || '')
  const command = useCommand()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newFlowName, setNewFlowName] = useState('')
  const [newFlowDescription, setNewFlowDescription] = useState('')
  const [newFlowColor, setNewFlowColor] = useState<string>(getRandomFlowColor())
  const [newFlowIsDefault, setNewFlowIsDefault] = useState(false)
  
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingFlowId, setEditingFlowId] = useState<string | null>(null)
  const [editFlowName, setEditFlowName] = useState('')
  const [editFlowDescription, setEditFlowDescription] = useState('')
  const [editFlowColor, setEditFlowColor] = useState<string>(getRandomFlowColor())
  const [editFlowOriginalColor, setEditFlowOriginalColor] = useState<string | undefined>(undefined)
  
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingFlow, setDeletingFlow] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    if (!scenarioId) {
      navigate('/')
    }
  }, [scenarioId, navigate])

  useEffect(() => {
    if (showCreateModal) {
      setNewFlowColor(getRandomFlowColor())
    }
  }, [showCreateModal])

  const handleCreateFlow = async () => {
    if (!newFlowName.trim() || !scenarioId) return

    try {
      const flowId = `flow_${crypto.randomUUID()}`
      await command.createFlow(
        scenarioId,
        flowId,
        newFlowName.trim(),
        newFlowDescription.trim() || undefined,
        newFlowColor
      )

      if (newFlowIsDefault) {
        await command.setDefaultFlow(scenarioId, flowId)
      }
      
      setNewFlowName('')
      setNewFlowDescription('')
      setNewFlowColor(getRandomFlowColor())
      setNewFlowIsDefault(false)
      setShowCreateModal(false)
      
      // Navigate to flow editor
      navigate(`/scenario/${scenarioId}/flow/${flowId}`)
    } catch (error) {
      console.error('Error creating flow:', error)
    }
  }

  const handleEditFlow = (flow: Flow) => {
    setEditingFlowId(flow.id)
    setEditFlowName(flow.name)
    setEditFlowDescription(flow.description || '')
    const resolved = resolveFlowColor(flow)
    setEditFlowColor(resolved)
    setEditFlowOriginalColor(resolved)
    setShowEditModal(true)
  }

  const handleSaveEdit = async () => {
    if (!editFlowName.trim() || !scenarioId || !editingFlowId) return

    try {
      const newColor = editFlowColor !== (editFlowOriginalColor ?? '')
        ? editFlowColor
        : undefined

      await command.renameFlow(
        scenarioId,
        editingFlowId,
        editFlowName.trim(),
        editFlowDescription.trim() || undefined,
        newColor
      )
      
      setEditingFlowId(null)
      setEditFlowName('')
      setEditFlowDescription('')
      setEditFlowColor(getRandomFlowColor())
      setEditFlowOriginalColor(undefined)
      setShowEditModal(false)
    } catch (error) {
      console.error('Error renaming flow:', error)
    }
  }

  const handleDeleteClick = (flowId: string, name: string) => {
    setDeletingFlow({ id: flowId, name })
    setShowDeleteModal(true)
  }

  const handleSetDefaultFlow = async (flowId: string) => {
    if (!scenarioId) return

    try {
      await command.setDefaultFlow(scenarioId, flowId)
    } catch (error) {
      console.error('Error setting default flow:', error)
    }
  }

  const handleConfirmDelete = async () => {
    if (!scenarioId || !deletingFlow) return

    try {
      await command.deleteFlow(scenarioId, deletingFlow.id)
      setDeletingFlow(null)
      setShowDeleteModal(false)
    } catch (error) {
      console.error('Error deleting flow:', error)
    }
  }

  if (scenarioLoading || flowsLoading) {
    return <LoadingSpinner />
  }

  if (!scenario) {
    return <div>Scenario not found</div>
  }

  const canEdit = scenario.role === 'owner' || scenario.role === 'editor'

  return (
    <div className="flows-container">
      <PageHeader
        kind="Flows"
        kindIcon={<FlowIcon size={14} />}
        tone="flow"
        title="Flows"
        subtitle={scenario.name}
        backLabel="Scenario"
        onBack={backToScenario}
      />

      <PwaUpdateBanner />

      {!canEdit && <AccessDeniedBanner />}

      <div className="flows-content">
        {flows.length === 0 ? (
          <div className="flows-empty">
            <p>No flows yet. Create your first flow to organize items.</p>
            {canEdit && (
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Create Flow
              </button>
            )}
          </div>
        ) : (
          <>
            {canEdit && (
              <div className="flows-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  + New Flow
                </button>
              </div>
            )}

            <div className="flows-list">
              {flows.map((flow: Flow) => (
                <div key={flow.id} className="flow-card" style={{ borderLeftColor: `var(--flow-accent-${resolveFlowColor(flow)})` }}>
                  <div
                    className="flow-card-content"
                    onClick={() => navigate(`/scenario/${scenarioId}/flow/${flow.id}`)}
                  >
                    <div className="flow-header">
                      <div style={{ opacity: 0.7, display: 'flex' }}>
                        <FlowIcon size={20} />
                      </div>
                      <h3 className="flow-name">
                        {flow.name}
                        {scenario.defaultFlowId === flow.id && <span className="flow-badge">Default</span>}
                      </h3>
                    </div>
                    {flow.description && (
                      <p className="flow-description">{flow.description}</p>
                    )}
                    <div className="flow-stats">
                      {Object.keys(flow.rankings).length} items ordered
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flow-card-actions">
                      <button
                        className="btn btn-icon"
                        onClick={(e) => {
                          e.stopPropagation()
                          void handleSetDefaultFlow(flow.id)
                        }}
                        title={scenario.defaultFlowId === flow.id ? 'Default flow' : 'Set as default flow'}
                        aria-label={scenario.defaultFlowId === flow.id ? 'Default flow' : 'Set as default flow'}
                      >
                        {scenario.defaultFlowId === flow.id ? '★' : '☆'}
                      </button>
                      <button
                        className="btn btn-icon"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditFlow(flow)
                        }}
                        title="Edit flow"
                      >
                        ✎
                      </button>
                      <button
                        className="btn btn-icon btn-danger"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteClick(flow.id, flow.name)
                        }}
                        title="Delete flow"
                      >
                        🗑
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Create Flow Modal */}
      <FormModal
        isOpen={showCreateModal}
        title="Create Flow"
        onClose={() => {
          setShowCreateModal(false)
          setNewFlowName('')
          setNewFlowDescription('')
          setNewFlowColor(getRandomFlowColor())
          setNewFlowIsDefault(false)
        }}
        onSubmit={handleCreateFlow}
        submitLabel="Create"
        name={newFlowName}
        onNameChange={setNewFlowName}
        nameLabel="Name"
        namePlaceholder="Flow name"
        description={newFlowDescription}
        onDescriptionChange={setNewFlowDescription}
        descriptionLabel="Description"
        descriptionPlaceholder="Optional description"
        showDescription={true}
      >
        <div className="form-group">
          <label>Color</label>
          <FlowColorPicker value={newFlowColor} onChange={setNewFlowColor} />
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={newFlowIsDefault}
              onChange={(e) => setNewFlowIsDefault(e.target.checked)}
            />
            <span>Set as default flow</span>
          </label>
        </div>
      </FormModal>

      {/* Edit Flow Modal */}
      <FormModal
        isOpen={showEditModal}
        title="Edit Flow"
        onClose={() => {
          setShowEditModal(false)
          setEditingFlowId(null)
          setEditFlowName('')
          setEditFlowDescription('')
          setEditFlowColor(getRandomFlowColor())
          setEditFlowOriginalColor(undefined)
        }}
        onSubmit={handleSaveEdit}
        submitLabel="Save"
        name={editFlowName}
        onNameChange={setEditFlowName}
        nameLabel="Name"
        namePlaceholder="Flow name"
        description={editFlowDescription}
        onDescriptionChange={setEditFlowDescription}
        descriptionLabel="Description"
        descriptionPlaceholder="Optional description"
        showDescription={true}
      >
        <div className="form-group">
          <label>Color</label>
          <FlowColorPicker value={editFlowColor} onChange={setEditFlowColor} />
        </div>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        title="Delete Flow"
        message={`Are you sure you want to delete "${deletingFlow?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setShowDeleteModal(false)
          setDeletingFlow(null)
        }}
      />
    </div>
  )
}
